// Stages
export const STAGES = ["beta", "prod"] as const;
export type Stage = typeof STAGES[number];

// Realms
export const REALMS_BETA = ["NA", "FE"] as const;
export const REALMS_PROD = ["NA", "FE", "FRA", "ZAZ"] as const;
export const REALMS = [...REALMS_PROD] as const;
export type Realm = typeof REALMS[number];
export type RealmLower = Lowercase<Realm>;

export const getRealmsForStage = (stage: Stage): readonly Realm[] => (stage === "beta" ? REALMS_BETA : REALMS_PROD);

// Table Names
export const TABLE_NAMES = ["rfd", "registration-request", "ab-registration", "ab-guest"] as const;
export type TableName = typeof TABLE_NAMES[number];

export const TABLE_LABELS: Record<TableName, string> = {
  "rfd": "Registration Form Data",
  "registration-request": "Registration Request",
  "ab-registration": "AB Registration",
  "ab-guest": "AB Guest",
};

// Navigation
export const NAV = {
  CONSOLE: "Console",
  SEARCH: "Search",
  REDRIVE: "Redrive",
  KEYMASTER: "KeyMaster",
  SFN_FAILURES: "Step Function Failures",
  ROUTES: {
    HOME: "#",
    SEARCH: "#/search",
    REDRIVE: "#/redrive",
    KEYMASTER: "#/keymaster",
    SFN_FAILURES: "#/sfn-failures",
  },
} as const;

export type Route = "search" | "keymaster" | "redrive" | "sfn-failures";

// Redrive Labels
export const REDRIVE_LABELS = {
  PAGE_TITLE: "Redrive",
  PAGE_DESCRIPTION: "Batch redrive step function executions",
  CONFIGURATION: "Configuration",
  RESULTS: "Results",
  STATE_MACHINE_NAME: "State Machine Name",
  STATE_MACHINE_PLACEHOLDER: "Enter state machine name",
  REALM: "Realm",
  START_DATE: "Start Date (UTC)",
  END_DATE: "End Date (UTC)",
  DATE_PLACEHOLDER: "YYYY/MM/DD",
  OVERRIDE: "Override",
  RESUME: "Resume",
  REDRIVE_FAILED: "Redrive Failed",
  REDRIVE_ABORTED: "Redrive Aborted",
  REDRIVE_TIMED_OUT: "Redrive Timed Out",
  REDRIVE_RUNNING: "Redrive Running",
  START_REDRIVE: "Start Redrive",
  CANCEL_POLLING: "Cancel Polling",
  POLLING_LABEL: "Polling for results...",
  PROD_WARNING_HEADER: "Production Environment",
  PROD_WARNING_MESSAGE: "You are about to redrive workflows in production. Proceed with caution.",
  WORKFLOW_ERROR: "Workflow Error",
  ATTEMPTED: "Attempted",
  SUCCESSFUL: "Successful",
  FAILED: "Failed",
  COMPLETE: "Complete",
  NEW_EXECUTIONS: "New Executions",
  ERRORS: "Errors",
  SHOWING_FIRST: "Showing the first",
  LOG_EVENTS: "Log Events",
  EXECUTION_ARN: "Execution ARN",
  FAILED_EXECUTION: "Failed Execution",
  STARTED: "Started",
  STATUS: "Status",
  ERROR: "Error",
  STATE_MACHINE_REQUIRED: "State machine name is required",
  REDRIVE_FAILED_ERROR: "Redrive failed",
} as const;

// Step Function Failures Labels
export const SFN_FAILURES_LABELS = {
  PAGE_TITLE: "Step Function Failures",
  PAGE_DESCRIPTION: "List failed executions of a step function within a timeframe, with their failure reasons",
  CONFIGURATION: "Configuration",
  RESULTS: "Results",
  STATE_MACHINE: "State Machine",
  STATE_MACHINE_PLACEHOLDER: "Select a state machine",
  STATE_MACHINES_LOADING: "Loading state machines...",
  STATE_MACHINES_ERROR: "Failed to load state machines",
  STATE_MACHINES_RECOVERY: "Retry",
  STATE_MACHINES_EMPTY: "No state machines found",
  REALM: "Realm",
  TIMEFRAME: "Timeframe (local time)",
  TIMEFRAME_PLACEHOLDER: "Filter by a date and time range",
  INCLUDE_FAILED: "Failed",
  INCLUDE_TIMED_OUT: "Timed Out",
  INCLUDE_ABORTED: "Aborted",
  STATUSES_LABEL: "Statuses",
  SEARCH: "Search",
  EXECUTION: "Execution",
  STATUS: "Status",
  STARTED: "Started",
  STOPPED: "Stopped",
  ERROR: "Error",
  CAUSE: "Cause",
  COPY: "Copy",
  COPY_ROW_ARIA_LABEL: "Copy failure details",
  COPY_ALL: "Copy results",
  COPIED: "Copied!",
  COPY_FAILED: "Failed to copy",
  NO_FAILURES: "No failed executions found in the selected timeframe",
  TRUNCATED_WARNING_HEADER: "Results truncated",
  TRUNCATED_WARNING: "Only the most recent 100 executions are shown. Narrow the timeframe to see all failures.",
  INVALID_RANGE: "Select a valid timeframe",
  RANGE_START_BEFORE_END: "The start date must be before the end date",
  RANGE_INCOMPLETE: "Select a start and end date",
  RANGE_DURATION_POSITIVE: "The duration must be greater than zero",
  STATUS_REQUIRED: "Select at least one status",
  SEARCH_FAILED: "Failure lookup failed",
} as const;

// KeyMaster Labels
export const KEYMASTER_LABELS = {
  PAGE_TITLE: "KeyMaster",
  PAGE_DESCRIPTION: "Encrypt or decrypt customer identifiers",
  REALM: "Realm",
  DATA_TYPE: "Data Type",
  ACTION: "Action",
  PLAIN_TEXT: "Plain Text",
  ENCRYPTED_TEXT: "Encrypted Text",
  SWAP: "Swap",
  PROD_WARNING_HEADER: "Production Environment",
  PROD_WARNING_MESSAGE: "You are using KeyMaster in production. Be careful with sensitive data.",
  SUCCESS_ENCRYPTED: "Encrypted successfully",
  SUCCESS_DECRYPTED: "Decrypted successfully",
  CONVERSION_FAILED: "Conversion failed",
} as const;

export enum KeyMasterDataType {
  FULL_NAME = "ABRegistration.contactInfo.fullName",
  BUSINESS_PHONE = "ABRegistration.businessInfo.businessPhone",
  BUSINESS_NAME = "ABRegistration.businessInfo.businessName",
  BUSINESS_IDENTIFIER = "ABRegistration.businessInfo.businessIdentifier.businessIdentifierValue",
  ADDITIONAL_LICENSE = "ABRegistration.businessInfo.additionalLicenseNumber",
  JOB_TITLE = "ABRegistration.contactInfo.jobTitle",
  TAX_ID = "ABRegistration.businessInfo.taxId.taxIdValue",
  DATE_OF_BIRTH = "ABRegistration.contactInfo.dateOfBirth",
  WORK_PHONE = "ABRegistration.contactInfo.workPhone",
  EMAIL = "ABRegistration.contactInfo.email",
  MOBILE_PHONE = "ABRegistration.contactInfo.mobilePhone",
  BUSINESS_ID = "ABRegistration.businessInfo.businessId.businessIdValue",
  EXPORT_DATA = "ABRegistration.exportData.fullMessage",
}

export const KEYMASTER_DATA_TYPE_OPTIONS = [
  { value: KeyMasterDataType.FULL_NAME, label: "Full Name" },
  { value: KeyMasterDataType.EMAIL, label: "Email" },
  { value: KeyMasterDataType.BUSINESS_NAME, label: "Business Name" },
  { value: KeyMasterDataType.BUSINESS_PHONE, label: "Business Phone" },
  { value: KeyMasterDataType.MOBILE_PHONE, label: "Mobile Phone" },
  { value: KeyMasterDataType.WORK_PHONE, label: "Work Phone" },
  { value: KeyMasterDataType.TAX_ID, label: "Tax ID" },
  { value: KeyMasterDataType.BUSINESS_IDENTIFIER, label: "Business Identifier" },
  { value: KeyMasterDataType.BUSINESS_ID, label: "Business ID" },
  { value: KeyMasterDataType.ADDITIONAL_LICENSE, label: "Additional License" },
  { value: KeyMasterDataType.JOB_TITLE, label: "Job Title" },
  { value: KeyMasterDataType.DATE_OF_BIRTH, label: "Date of Birth" },
  { value: KeyMasterDataType.EXPORT_DATA, label: "Export Data" },
] as const;

// UI Labels
export const LABELS = {
  CUSTOMER_SEARCH: "Customer Search",
  CUSTOMER_DATA: "Customer Data",
  ACCOUNT_STATUS: "Account Status",
  WORKFLOWS: "Workflows",
  SEARCH_RESULTS: "Search Results",
  EXECUTION_DETAILS: "Execution Details",
  EXECUTION_ARN: "Execution ARN",
  VERSION_HISTORY: "Version History",
  CURRENT_DATA: "Current Data",
  CUSTOMER_ID: "Customer ID",
  REALM: "Realm",
  INCLUDE_HISTORY: "Include version history",
  SEARCH: "Search",
  STATUS: "Status",
  REGISTRATION: "Registration",
  SHUMA: "SHUMA",
  GUEST: "Guest",
  INTERNAL: "Internal",
  STARTED: "Started",
  COMPLETED: "Completed",
  DURATION: "Duration",
  VIEW_DETAILS: "View Details",
  CONSOLE: "Console",
  REDRIVE: "Redrive",
  REDRIVE_WORKFLOW: "Redrive Workflow",
  OPEN_IN_CONSOLE: "Open in Console",
  COPY_JSON: "Copy JSON",
  COPIED: "Copied!",
  INPUT: "Input",
  OUTPUT: "Output",
  SHOW_DIFF_VIEW: "Show diff view",
  TABLES_FOUND: "Tables Found",
  HISTORY_VERSIONS: "History Versions",
  FAILED_WORKFLOWS: "Failed Workflows",
  NO_DATA_AVAILABLE: "No data available",
  NO_WORKFLOWS_FOUND: "No workflows found",
  NO_WORKFLOW_EXECUTIONS: "No workflow executions found for this customer",
  NO_VERSION_HISTORY: "No version history available",
  NO_RESULTS_FOUND: "No results found",
  RENDER_ERROR_HEADER: "Something went wrong",
  RENDER_ERROR_MESSAGE: "This page failed to render. Try another page or reload the console.",
  SESSION_EXPIRED: "Session expired. Reload the console to sign in again.",
  CUSTOMER_DATA_ERROR: "Customer data lookup failed",
  WORKFLOWS_ERROR: "Workflow lookup failed",
  SCHEDULED_EVENTS_ERROR: "Scheduled events lookup failed",
  EXECUTION_IN_PROGRESS: "Execution in progress...",
  NO_INPUT_DATA: "No input data",
  NO_OUTPUT_DATA: "No output data",
  NO_RFD_DATA: "No RFD data found",
  SEARCH_DESCRIPTION: "Search for customer registration data across all tables and view workflow executions",
  SEARCHING: "Searching across all tables and workflows...",
  CUSTOMER_ID_PLACEHOLDER: "Enter customer ID or encrypted ID",
  SELECT_VERSION: "Select version",
  CURRENT: "Current",
  INITIAL: "Initial",
  VERSION: "Version",
  RUNNING: "Running...",
  IN_HISTORY: "in history",
  RESULTS_FOR: "Results for",
  EXECUTION_ERROR: "Execution Error",
  ERROR: "Error",
  CAUSE: "Cause",
  PREVIOUS: "Previous",
  SCHEDULED_EVENTS: "Scheduled Events",
  SCHEDULE_GROUP: "Schedule Group",
  FIRE_DATE: "Fire Date",
  NO_SCHEDULED_EVENTS: "No scheduled events found",
  TARGET: "Target",
  EMPTY_VALUE: "-",
  OPENS_IN_NEW_TAB: "Opens in a new tab",
  COPY_RESULTS: "Copy results",
  RECENT_SEARCHES: "Recent searches:",
  CLEAR: "Clear",
  LOCAL_TIME: "Local",
  UTC_TIME: "UTC",
  TIME_DISPLAY: "Time display",
} as const;

// Status Values
export const REGISTRATION_STATUSES = {
  REGISTERED: "REGISTERED",
  REGISTRATION_PENDING: "REGISTRATION_PENDING",
  REGISTRATION_FAILED: "REGISTRATION_FAILED",
  REGISTRATION_REJECTED: "REGISTRATION_REJECTED",
  CANCELLED: "CANCELLED",
} as const;

export const STATUS_LABELS = {
  REGISTERED: "Registered",
  PENDING: "Pending",
  FAILED: "Failed",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
  UNKNOWN: "Unknown",
  SHUMA: "SHUMA",
  NOT_SHUMA: "Not SHUMA",
  GUEST: "Guest",
  NOT_GUEST: "Not Guest",
  INTERNAL: "Internal",
  NOT_INTERNAL: "External",
} as const;

export const STATUS_DESCRIPTIONS = {
  REGISTERED: "Customer is fully registered",
  PENDING: "Registration in progress",
  FAILED: "Registration failed",
  REJECTED: "Registration was rejected",
  CANCELLED: "Registration was cancelled",
  SHUMA: "Single Household User with Multiple Accounts",
  GUEST: "Guest account",
  NOT_GUEST: "Full account",
  INTERNAL: "Amazon internal account",
  NOT_INTERNAL: "External customer account",
} as const;

export const DEFAULT_REGION = "us-west-2";

// Shared notification type used across pages and hooks
export type NotificationType = "success" | "error" | "info" | "warning";
export type NotifyFn = (type: NotificationType, message: string) => void;
